import { ParticipantBroadcastDialog } from '@/components/ParticipantBroadcastDialog';
import { ParticipantFormDialog } from '@/components/ParticipantFormDialog';
import { ParticipantImportDialog } from '@/components/ParticipantImportDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEvents } from '@/hooks/use-events';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  type CreateEventParticipantInput,
  type EventParticipant,
  EventParticipant as EventParticipantSchema,
  type ParticipantsListResponse,
  ParticipantsListResponse as ParticipantsListResponseSchema,
  type UpdateEventParticipantInput,
} from '@tunodo/ticketing-shared';
import { Award, MoreHorizontal, Pencil, Plus, Send, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

const SearchParamsSchema = z.object({
  eventId: z.string().uuid().optional(),
});
type SearchParams = z.infer<typeof SearchParamsSchema>;

const ALL_EVENT_SENTINEL = '__all__';

export const Route = createFileRoute('/_app/participants/')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden ver la lista de ponentes.');
      throw redirect({ to: '/' });
    }
  },
  validateSearch: (search: Record<string, unknown>): SearchParams =>
    SearchParamsSchema.parse(search),
  component: ParticipantsPage,
});

function ParticipantsPage() {
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const { eventId } = Route.useSearch();

  const { events } = useEvents();

  const [createOpen, setCreateOpen] = useState(false);
  const [editParticipant, setEditParticipant] = useState<EventParticipant | null>(null);
  const [deleteParticipant, setDeleteParticipant] = useState<EventParticipant | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const resolvedEventId = eventId ?? (events.length === 1 ? events[0]?.id : undefined);

  const queryKey = ['participants', resolvedEventId];
  const { data, isLoading, isError } = useQuery<ParticipantsListResponse>({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (resolvedEventId) params.set('eventId', resolvedEventId);
      return apiFetch(`/api/participants?${params.toString()}`, {
        schema: ParticipantsListResponseSchema,
      });
    },
    enabled: !!resolvedEventId,
  });

  const participants = data ?? [];

  const createMutation = useMutation({
    mutationFn: (input: CreateEventParticipantInput) =>
      apiFetch('/api/participants', {
        method: 'POST',
        body: JSON.stringify(input),
        schema: EventParticipantSchema,
      }),
    onSuccess: () => {
      toast.success('Ponente creado exitosamente');
      void queryClient.invalidateQueries({ queryKey });
      setCreateOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEventParticipantInput }) =>
      apiFetch(`/api/participants/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        schema: EventParticipantSchema,
      }),
    onSuccess: () => {
      toast.success('Ponente actualizado exitosamente');
      void queryClient.invalidateQueries({ queryKey });
      setEditParticipant(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/participants/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Ponente eliminado');
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const sendOneMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ messageId: string | null; sentAt: string }>(
        `/api/participants/${id}/send-certificate`,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => toast.success('Certificado enviado'),
    onError: (err: Error) => toast.error(err.message),
  });

  async function handleCreate(input: CreateEventParticipantInput | UpdateEventParticipantInput) {
    await createMutation.mutateAsync(input as CreateEventParticipantInput);
  }

  async function handleUpdate(input: CreateEventParticipantInput | UpdateEventParticipantInput) {
    if (!editParticipant) return;
    await updateMutation.mutateAsync({
      id: editParticipant.id,
      input: input as UpdateEventParticipantInput,
    });
  }

  function setSearchParam(updates: Partial<SearchParams>) {
    void navigate({ search: (s) => ({ ...s, ...updates }) });
  }

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ponentes</h1>
          <div className="text-muted-foreground mt-1 text-sm">
            {isLoading ? (
              <Skeleton className="h-4 w-28" />
            ) : (
              <>
                {participants.length} ponente{participants.length !== 1 ? 's' : ''}
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setBroadcastOpen(true)}
            disabled={!resolvedEventId}
            title={!resolvedEventId ? 'Selecciona un evento para enviar certificados' : undefined}
          >
            <Award className="h-4 w-4" />
            Enviar certificados
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setImportOpen(true)}
            disabled={!resolvedEventId}
            title={!resolvedEventId ? 'Selecciona un evento para importar' : undefined}
          >
            <Upload className="h-4 w-4" />
            Importar CSV
          </Button>
          <Button
            className="gap-2"
            onClick={() => setCreateOpen(true)}
            disabled={!resolvedEventId}
            title={!resolvedEventId ? 'Selecciona un evento primero' : undefined}
          >
            <Plus className="h-4 w-4" />
            Nuevo ponente
          </Button>
        </div>
      </div>

      {/* Event selector (show when multiple events) */}
      {events.length > 1 && (
        <div className="flex items-center gap-3">
          <Select
            value={resolvedEventId ?? ALL_EVENT_SENTINEL}
            onValueChange={(val) =>
              setSearchParam({ eventId: val === ALL_EVENT_SENTINEL ? undefined : val })
            }
          >
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Selecciona un evento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_EVENT_SENTINEL}>Todos los eventos</SelectItem>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id}>
                  {event.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Single event label */}
      {events.length === 1 && events[0] && (
        <p className="text-sm text-muted-foreground">
          Evento: <span className="font-medium text-foreground">{events[0].name}</span>
        </p>
      )}

      {/* No event selected */}
      {!resolvedEventId && events.length > 1 && (
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground text-sm">
          Selecciona un evento para ver sus ponentes.
        </div>
      )}

      {/* Loading */}
      {isLoading && resolvedEventId && (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {['Nombre', 'Email', 'Teléfono', 'Bio', 'Código', ''].map((h) => (
                  <TableHead key={h}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 4 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: skeleton cells
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full max-w-[100px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-6 text-center space-y-3">
          <p className="text-sm text-destructive font-medium">
            No se pudo cargar la lista de ponentes.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void queryClient.invalidateQueries({ queryKey })}
          >
            Reintentar
          </Button>
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && resolvedEventId && (
        <div className="rounded-md border overflow-x-auto">
          <Table style={{ minWidth: '700px' }}>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Bio</TableHead>
                <TableHead>Código</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {participants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    Aún no hay ponentes.{' '}
                    <button
                      type="button"
                      className="underline text-primary hover:no-underline"
                      onClick={() => setCreateOpen(true)}
                    >
                      Agrega el primero
                    </button>
                    .
                  </TableCell>
                </TableRow>
              ) : (
                participants.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.fullName}</TableCell>
                    <TableCell className="text-muted-foreground">{p.email}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.phone ?? <span className="opacity-40">—</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px]">
                      {p.bio ? (
                        <span className="truncate block" title={p.bio}>
                          {p.bio.length > 60 ? `${p.bio.slice(0, 60)}…` : p.bio}
                        </span>
                      ) : (
                        <span className="opacity-40">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {p.shortCode}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Acciones</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => sendOneMutation.mutate(p.id)}
                            disabled={sendOneMutation.isPending}
                          >
                            <Send className="h-4 w-4" />
                            Enviar certificado
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditParticipant(p)}>
                            <Pencil className="h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteParticipant(p)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialogs */}
      {resolvedEventId && (
        <>
          <ParticipantFormDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            mode="create"
            eventId={resolvedEventId}
            onSubmit={handleCreate}
            pending={createMutation.isPending}
          />

          {editParticipant && (
            <ParticipantFormDialog
              open={!!editParticipant}
              onOpenChange={(v) => {
                if (!v) setEditParticipant(null);
              }}
              mode="edit"
              participant={editParticipant}
              eventId={resolvedEventId}
              onSubmit={handleUpdate}
              pending={updateMutation.isPending}
            />
          )}

          <ParticipantImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            eventId={resolvedEventId}
          />

          <ParticipantBroadcastDialog
            open={broadcastOpen}
            onOpenChange={setBroadcastOpen}
            eventId={resolvedEventId}
            total={participants.length}
          />
        </>
      )}

      {deleteParticipant && (
        <ConfirmDialog
          open={!!deleteParticipant}
          onOpenChange={(v) => {
            if (!v) setDeleteParticipant(null);
          }}
          title="Eliminar ponente"
          description={`¿Estás seguro de que deseas eliminar a "${deleteParticipant.fullName}"? Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          variant="destructive"
          onConfirm={() => {
            deleteMutation.mutate(deleteParticipant.id);
            setDeleteParticipant(null);
          }}
        />
      )}
    </main>
  );
}
